import React, { CSSProperties } from 'react'
import { DataGridProps } from '../models'
import PropTypes from 'prop-types'
import { useEffectUpdate } from '@src/hooks'

export const defaultButton = props => (
  <button type="button" {...props} className="-btn">
    {props.children}
  </button>
)

defaultButton.propTypes = { children: PropTypes.node.isRequired }

export interface PaginationProps<T, PluginOptions>
  extends DataGridProps<T, PluginOptions> {
  onPageChange: (event: any[]) => void
  pageJumpText: string
  pageSize: number
  pageSizeOptions: number[]
  showPageJump: boolean
  canPrevious: boolean
  onPageSizeChange?: (event: any) => void
  nextText: string
  previousText: string
  rowsText: string
  rowsSelectorText: string
  ofText: string
  pageText: string
  style: CSSProperties
  PreviousComponent: React.ElementType
  NextComponent: React.ElementType
  renderPageJump: (any) => any
  renderCurrentPage: (any) => any
  renderTotalPagesCount: (any) => any
  renderPageSizeOptions: (options: any) => void
}

interface State<T> {
  page: any
}

function ImprovedPagination<T, PluginOptions>(
  props: PaginationProps<T, PluginOptions>
) {
  const {
    pages,
    onPageChange,
    showPageSizeOptions,
    pageSizeOptions,
    previousText,
    pageSize,
    canPrevious,
    canNext,
    pageText,
    ofText,
    onPageSizeChange,
    rowsSelectorText,
    rowsText,
    nextText,
    showPageJump = true,
    PreviousComponent = defaultButton,
    NextComponent = defaultButton,
    renderPageJump = ({
      onChange,
      value,
      onBlur,
      onKeyPress,
      inputType,
      pageJumpText
    }) => (
      <div className="-pageJump">
        <input
          aria-label={pageJumpText}
          type={inputType}
          onChange={onChange}
          value={value}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
        />
      </div>
    ),
    renderCurrentPage = page => (
      <span className="-currentPage">{page + 1}</span>
    ),
    renderTotalPagesCount = pages => (
      <span className="-totalPages">{pages || 1}</span>
    ),
    renderPageSizeOptions = ({
      pageSize,
      pageSizeOptions,
      rowsSelectorText,
      onPageSizeChange,
      rowsText
    }) => (
      <span className="select-wrap -pageSizeOptions">
        <select
          aria-label={rowsSelectorText}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          value={pageSize}
        >
          {pageSizeOptions.map((option, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <option key={i} value={option}>
              {`${option} ${rowsText}`}
            </option>
          ))}
        </select>
      </span>
    )
  } = props

  const [state, setState] = React.useState<State<T>>({
    page: props.page
  })

  useEffectUpdate(() => {
    setState(prev => ({
      page: pages <= prev?.page ? props.page - 1 : props.page
    }))
  }, [props.page])

  function getSafePage(page) {
    if (Number.isNaN(page)) {
      page = props.page
    }
    return Math.min(Math.max(page, 0), pages - 1)
  }

  function changePage(page) {
    page = getSafePage(page)
    setState({ page })
    if (props.page !== page) {
      onPageChange(page)
    }
  }

  function applyPage(e) {
    if (e) {
      e.preventDefault()
    }
    const page = state.page
    changePage(page.toString() === '' ? props.page : page)
  }

  function getPageJumpProperties() {
    return {
      onKeyPress: e => {
        if (e.which === 13 || e.keyCode === 13) {
          applyPage(e)
        }
      },
      onBlur: applyPage,
      value: state.page.toString() === '' ? '' : state.page + 1,
      onChange: e => {
        const val = e.target.value
        const page = val - 1
        if (val === '') {
          return setState({ page: val })
        }
        setState({ page: getSafePage(page) })
        changePage(page)
      },
      inputType: state.page.toString() === '' ? 'text' : 'number',
      pageJumpText: props.pageJumpText
    }
  }

  return (
    <div className="-pagination" style={props.style}>
      <div className="-firstPage" style={{ marginRight: '1px' }}>
        <button
          type="button"
          className="-btn"
          onClick={() => {
            changePage(0)
          }}
          disabled={!canPrevious || state.page < 1}
        >
          <i className="material-icons">first_page</i>
        </button>
      </div>
      <div className="-previous">
        <PreviousComponent
          onClick={() => {
            if (!canPrevious) return
            changePage(props.page - 1)
          }}
          disabled={!canPrevious || state.page < 1}
        >
          {previousText}
        </PreviousComponent>
      </div>
      <div className="-center">
        <span className="-pageInfo">
          {pageText}{' '}
          {showPageJump
            ? renderPageJump(getPageJumpProperties())
            : renderCurrentPage(props.page)}{' '}
          {ofText} {renderTotalPagesCount(pages)}
        </span>
        {showPageSizeOptions &&
          renderPageSizeOptions({
            pageSize,
            rowsSelectorText,
            pageSizeOptions,
            onPageSizeChange,
            rowsText
          })}
      </div>
      <div className="-next">
        <NextComponent
          onClick={() => {
            if (!canNext) return
            changePage(props.page + 1)
          }}
          disabled={!canNext || state.page >= props.pages - 1}
        >
          {nextText}
        </NextComponent>
      </div>
      <div className="-lastPage">
        <button
          type="button"
          className="-btn"
          onClick={() => {
            changePage(props.pages)
          }}
          style={{ marginLeft: '1px' }}
          disabled={state.page >= props.pages - 1}
        >
          <i className="material-icons">last_page</i>
        </button>
      </div>
    </div>
  )
}

export default ImprovedPagination
